import { Box } from '@material-ui/core';
import { BorrowerOnboardingDetails, DocumentId } from '@nemuru-eng/js-api-client';
import { Dispatch, SetStateAction } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  NIE_EU_CARD_ID,
  NIE_EU_EXPIRATION_DATE_CARD_DATE,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/constants/nieEuData.constants';
import {
  isNieDocumentType,
  isNieEuDocumentType,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/utils/nieDocumentTypes.utils';
import { mapNieDataToFormValues } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/PaymentIntent/Multilender/MerchantPos/_shared/constants/nieDataFormBlankValues';
import { DocumentIdForm } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/components/DocumentIdForm';
import { NieDataForm } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/components/NieDataForm';
import { DocumentIdFormType } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/schemas/DocumentIdFormSchema';
import {
  DocumentIdWithNieDataFormSchema,
  DocumentIdWithNieDataFormType,
} from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/schemas/documentIdWithNieDataForm.schema';
import { NieDataFormType } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/schemas/nieDataForm.schema';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { AddressMap } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/maps/address.map';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { useNotificationsContext } from '@/contexts/NotificationsContext/NotificationsContext';

interface DocumentIdEditDialogProps {
  documentId: DocumentId;
  opened: boolean;
  setOpened: Dispatch<SetStateAction<boolean>>;
  toggleDialog: () => void;
  loading: boolean;
  patchLoanUserDetails?: (data) => Promise<void>;
  patchNieData?: (data: NieDataFormType) => Promise<void>;
  showBirthDateField?: boolean;
  nieData?: BorrowerOnboardingDetails['nieData'];
  nieDataRequired: boolean;
}

interface NieDataSectionProps {
  nieData?: BorrowerOnboardingDetails['nieData'];
  nieDataRequired: boolean;
}

const NieDataSection = ({ nieData, nieDataRequired: nieDataRequired }: NieDataSectionProps) => {
  const { watch } = useFormContext();
  const documentType = watch('documentType');

  if (!nieDataRequired || !isNieDocumentType(documentType)) return null;

  return (
    <Box pt={2}>
      <NieDataForm
        documentType={documentType}
        nieData={nieData}
        fieldVariant={FieldVariant.form}
        direction="column"
        fullWidth={true}
      />
    </Box>
  );
};

export const DocumentIdEditDialog = ({
  documentId,
  opened,
  setOpened,
  patchLoanUserDetails,
  patchNieData,
  toggleDialog,
  loading,
  showBirthDateField,
  nieData,
  nieDataRequired,
}: DocumentIdEditDialogProps) => {
  const documentIdDefaultValues = {
    firstName: documentId.fullName.firstName,
    lastName: documentId.fullName.lastName,
    documentType: documentId.documentType,
    documentNumber: documentId.documentNumber,
    gender: documentId.gender,
    documentExpeditor: documentId.documentExpeditor,
    nationality: documentId.nationality,
    birthDate: documentId.birthDate,
    expiration: documentId.expiration,
    birthPlace: documentId.birthPlace,
    documentAddress: AddressMap.toForm(documentId.documentAddress),
    ...(nieDataRequired && nieData ? mapNieDataToFormValues(nieData) : {}),
  };

  const {
    actions: { resetSnackbarNotification },
  } = useNotificationsContext();

  const documentIdPreMapper = (useFormValues: DocumentIdFormType) => {
    return {
      fullName: {
        firstName: useFormValues.firstName,
        lastName: useFormValues.lastName,
      },
      documentExpeditor: useFormValues.documentExpeditor,
      documentNumber: useFormValues.documentNumber,
      documentType: useFormValues.documentType,
      nationality: useFormValues.nationality,
      expiration: useFormValues.expiration,
      birthDate: showBirthDateField ? useFormValues.birthDate : documentId.birthDate,
      birthPlace: useFormValues.birthPlace,
      gender: useFormValues.gender,
      documentAddress: AddressMap.toDto(useFormValues.documentAddress),
    };
  };

  const nieDataPreMapper = (useFormValues: DocumentIdWithNieDataFormType): NieDataFormType => {
    const isNieEu = isNieEuDocumentType(useFormValues.documentType);

    if (
      useFormValues.nationalityPeriod == null ||
      useFormValues.socialSecurityDate == null ||
      useFormValues.numberOfWork == null ||
      (!isNieEu && (useFormValues.cardId == null || useFormValues.expirationDateCard == null))
    ) {
      throw new Error('Nie data is invalid');
    }

    return {
      nationalityPeriod: useFormValues.nationalityPeriod,
      cardId: isNieEu ? NIE_EU_CARD_ID : useFormValues.cardId!,
      expirationDateCard: isNieEu ? NIE_EU_EXPIRATION_DATE_CARD_DATE : useFormValues.expirationDateCard!,
      socialSecurityDate: useFormValues.socialSecurityDate,
      numberOfWork: useFormValues.numberOfWork,
    };
  };

  const onSubmitForm = async (useFormValues: DocumentIdFormType | DocumentIdWithNieDataFormType) => {
    try {
      if (patchLoanUserDetails) {
        const preMappedDocumentIdValues = documentIdPreMapper(useFormValues);
        await patchLoanUserDetails({ userDetails: { documentId: preMappedDocumentIdValues } });
      }
    } catch (error) {
      throw new Error(`Unable to update borrower onboarding details: ${error}`);
    }
    const shouldPatchNieData = nieDataRequired && patchNieData && isNieDocumentType(useFormValues.documentType);

    if (shouldPatchNieData) {
      resetSnackbarNotification();
      try {
        await patchNieData(nieDataPreMapper(useFormValues as DocumentIdWithNieDataFormType));
      } catch (error) {
        toggleDialog();
        throw new Error(`Unable to patch nie data: ${error}`);
      }
    }
    toggleDialog();
  };

  return (
    <EditDialogWrapper
      opened={opened}
      setOpened={setOpened}
      handleClose={toggleDialog}
      dialogProps={{
        maxWidth: 'sm',
        fullWidth: true,
      }}
      title={'EDIT_DOCUMENT_ID'}
      onSubmitForm={onSubmitForm}
      isLoading={loading}
      formSchema={(t) => DocumentIdWithNieDataFormSchema(t, !!showBirthDateField, nieDataRequired)}
      defaultValues={documentIdDefaultValues}
    >
      <>
        <DocumentIdForm
          documentId={documentId}
          fieldVariant={FieldVariant.form}
          direction="column"
          fullWidth={true}
          showDetailedAddressForm
          showBirthDateField={showBirthDateField}
        />
        <NieDataSection nieData={nieData} nieDataRequired={nieDataRequired} />
      </>
    </EditDialogWrapper>
  );
};
